<template>
  <div>
    <QuoteSummary
      handleBackBtnExternally
      :cover="getCover"
      :isShowSaveAndExitButton="true"
      @click="customiseQuote"
      @showReplacementProductDialog="showReplacementProductDialog"
      @click:backButton="back"
      @showDrawerChangeCover="
        isShowDrawerChangeCover = !isShowDrawerChangeCover
      "
      heading="Your Home Insurance quotes"
    ></QuoteSummary>

    <DrawerChangeCover
      :coverTypeItems="
        $vuetify.breakpoint.lgAndUp
          ? COVER_TYPE_ITEMS_HOME
          : COVER_TYPE_ITEMS_HOME_SMALL
      "
      v-model="isShowDrawerChangeCover"
    />

    <div class="d-flex pl-4 mt-6 align-center pr-4 flex-wrap">
      <div
        class="d-flex flex-column flex-lg-row flex-wrap gc-5 gr-2"
        v-if="isShowPremiumFlex"
      >
        <div class="d-flex align-center gc-2">
          <h3 class="d-none d-md-block font-20">Premium flex</h3>
          <h3 class="d-md-none font-20">Flex</h3>
          <BaseTooltip
            :text="TOOL_TIP_TEXT.PremiumFlex"
            icon="mdi-help-circle"
          />
        </div>

        <FlexSpinner
          @input="calculateFlex"
          class="font-18"
          type="percentage"
          :maxCommissionSacrifice="getMaxCommissionSacrifice"
          :initialVal="commissionSacrifice"
        />
      </div>
      <div
        class="d-flex flex-column align-sm-end flex-md-row ml-auto order-md-3"
      >
        <BaseButton
          text="Share all"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex align-center mr-xl-9"
          icon="mdi-share-variant"
          color="#106175"
          small
          @click="shareAllQuotes"
        />
        <BaseButton
          text="Print all"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex align-center mt-1 mt-md-0"
          icon="mdi-printer"
          color="#106175"
          small
          @click="printAllQuotes"
        />
      </div>
    </div>

    <TheQuotes
      handleApplyBtnExternally
      :productGroup="productGroup"
      @shareQuote="shareQuote"
      @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
      @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
      @showEndorsementDetails="showEndorsementDetails"
      @showWhatsIncluded="showWhatsIncluded"
      @printQuotes="printQuote"
      @setAd="setAd"
      @showPayMonthly="showPayMonthly"
      @click:applyBtn="applyForQuote"
    />

    <DrawerCustomiseCover
      v-model="isShowDrawerCustomiseCover"
      :cover="getCover"
      :additionalCover="ADD_ONS"
      :additionalCoverAnnualCost="ADD_ONS_ANNUAL_COST"
      :productGroup="productGroup"
      @refreshCover="refreshCover"
      @editCover="isShowDrawerChangeCover = !isShowDrawerChangeCover"
    ></DrawerCustomiseCover>

    <ShareQuotesDialog ref="shareQuotes" :cover="getCover" />

    <PrintQuotesDialog ref="printQuotes" />

    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="endorsementDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pt-6 pb-3"
      headerDivider
    >
      <template v-slot:content="{ data }">
        <template v-if="data?.Description">
          <div v-html="data?.Description" class="pa-10"></div>
        </template>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="payMonthly"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      dark2Icon
    >
      <template v-slot:content>
        <DialogContentPayMonthly />
      </template>
    </BaseDialog>

    <BadgeDefaqto :show="isShowBadgeDefaqto" />

    <ProductTransferDialog
      ref="productTransfer"
      @setAd="setAd"
      @refreshCover="refreshCover"
    ></ProductTransferDialog>

    <BaseDialog
      ref="whatsIncluded"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
      :max-width="720"
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedAccidentalDamage
          @closeWhatsIncludedDialog="closeWhatsIncludedDialog"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { includesBuildings, includesContents } from "@/common/answers";
import { adStringToBool } from "@/common/cover";
import { getProductGroup } from "@/common/question-set";
import {
  ADD_ONS,
  ADD_ONS_ANNUAL_COST,
} from "@/constants/additional-cover-options";
import { TOOL_TIP_TEXT } from "@/constants/common.constants.js";
import { ROUTE_NAME } from "@/constants/route.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
import {
  COVER_TYPE_ITEMS_HOME,
  COVER_TYPE_ITEMS_HOME_SMALL,
} from "@/constants/component.constants.js";

export default {
  name: "B2BHomeYourQuotesPage",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    FlexSpinner: () => import("@/components/QuoteResult/FlexSpinner.vue"),
    QuoteSummary: () => import("@/components/QuoteResult/QuoteSummary.vue"),
    BaseTooltip: () => import("@/components/BaseComponents/BaseTooltip.vue"),
    TheQuotes: () => import("@/components/QuoteResult/TheQuotes.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    ShareQuotesDialog: () => import("@/views/Dialog/ShareQuotesDialog.vue"),
    PrintQuotesDialog: () => import("@/views/Dialog/PrintQuotesDialog.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    DialogContentPayMonthly: () =>
      import("@/components/QuoteResult/DialogContentPayMonthly"),
    DrawerCustomiseCover: () =>
      import("@/components/QuoteResult/DrawerCustomiseCover.vue"),
    ProductTransferDialog: () =>
      import("@/views/Dialog/ProductTransferDialog.vue"),
    BadgeDefaqto: () => import("@/components/QuoteResult/BadgeDefaqto.vue"),
    DialogContentWhatsIncludedAccidentalDamage: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedAccidentalDamage.vue"
      ),
    DrawerChangeCover: () =>
      import("@/components/QuoteResult/DrawerChangeCover.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      TOOL_TIP_TEXT,
      ADD_ONS,
      ADD_ONS_ANNUAL_COST,
      isShowDrawerCustomiseCover: false,
      isShowDrawerChangeCover: false,
      COVER_TYPE_ITEMS_HOME,
      COVER_TYPE_ITEMS_HOME_SMALL,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getCover",
      "getQuoteRequestId",
      "httpConfig",
      "insurerQuotesModel",
      "isShowPremiumFlex",
      "callChannel",
      "getMaxCommissionSacrifice",
      "replacementProductAvailable",
      "qsId",
      "selectedAds",
    ]),
    ...mapState(QUESTIONS_MODULE, ["commissionSacrifice", "questionMap"]),
    productGroup() {
      return getProductGroup(this.qsId);
    },
    isShowBadgeDefaqto() {
      if (this.isShowDrawerCustomiseCover) return false;
      if (this.isShowDrawerChangeCover) return false;
      return true;
    },
  },
  // clone a new quote any time we go back to step 1 or 2
  beforeRouteLeave(to, _from, next) {
    if (
      to.name === ROUTE_NAME.HOME.B2B.PROPERTY_DETAILS ||
      to.name === ROUTE_NAME.HOME.B2B.YOUR_DETAILS
    ) {
      this.cloneQuote();
    }
    next();
  },
  mounted() {
    // Restore accidental damage selections
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "updateCommissionSacrifice",
      "setCoverSelection",
      "saveQuoteResults",
      "setRestoringLastStep",
      "cloneQuote",
    ]),
    applyForQuote() {
      this.$router.push({ name: ROUTE_NAME.HOME.B2B.APPLICATION });
    },
    calculateFlex(val) {
      this.updateCommissionSacrifice(val);
    },
    back() {
      this.$router.push({ name: ROUTE_NAME.HOME.B2B.PROPERTY_DETAILS });
    },
    setAd(data) {
      this.setCoverSelection({
        buildingsAd: data.buildingsAd,
        contentsAd: data.contentsAd,
      });
    },
    shareAllQuotes() {
      this.$refs.shareQuotes.shareQuote("all");
    },
    shareQuote(quoteId) {
      this.$refs.shareQuotes.shareQuote("single", quoteId);
    },
    printAllQuotes() {
      this.$refs.printQuotes.printQuote("all");
    },
    printQuote(quote) {
      this.$refs.printQuotes.printQuote("single", quote);
    },
    customiseQuote() {
      if (this.replacementProductAvailable) {
        this.showReplacementProductDialog();
      } else {
        this.isShowDrawerCustomiseCover = true;
      }
    },
    showReplacementProductDialog() {
      this.$refs.productTransfer.open();
    },
    showEndorsementDetails(endorsement) {
      this.$refs.endorsementDetails.open(endorsement.Title, endorsement);
    },
    showPayMonthly() {
      this.$refs.payMonthly.open("More details");
    },
    refreshCover() {
      let buildingsAd = adStringToBool(this.selectedAds.buildingsAd);
      if (
        buildingsAd == null &&
        includesBuildings(this.questionMap.CoverType)
      ) {
        buildingsAd = false;
      }
      let contentsAd = adStringToBool(this.selectedAds.contentsAd);
      if (contentsAd == null && includesContents(this.questionMap.CoverType)) {
        contentsAd = false;
      }
      // Update global state
      this.setCoverSelection({ buildingsAd, contentsAd });
      // Update local models
      this.insurerQuotesModel.forEach((insurer) => {
        insurer.contentsAd = contentsAd;
        insurer.buildingsAd = buildingsAd;
        this.setInsurerQuote(insurer);
      });
    },
    setInsurerQuote(insurer) {
      // Update the local insurer model if it exists (updates screen)
      if (insurer.isAvailable && insurer.quotes) {
        insurer.selectedQuote = insurer.quotes.find(
          (q) =>
            q.BuildingsAD === insurer.buildingsAd &&
            q.ContentsAD === insurer.contentsAd
        );
      }
    },
    showWhatsIncluded() {
      this.$refs.whatsIncluded.open("What's included?");
    },
    closeWhatsIncludedDialog() {
      this.$refs.whatsIncluded.cancel();
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .additional-content {
    border-top: 1px solid #ccd6dd;
  }
}
</style>
